// ===========================================================
// ERP STOCK AGING MODULE (Dynamic with Inventory Items API + Pagination)
// ===========================================================

// Aging buckets configuration
const AGING_BUCKETS = [
  { label: '1-30 Days', min: 0, max: 30, color: '#3b82f6' },
  { label: '31-60 Days', min: 31, max: 60, color: '#10b981' },
  { label: '61-90 Days', min: 61, max: 90, color: '#f59e0b' },
  { label: '91-180 Days', min: 91, max: 180, color: '#ef4444' },
  { label: '181-360 Days', min: 181, max: 360, color: '#dc2626' },
  { label: '361-520 Days', min: 361, max: 520, color: '#991b1b' },
  { label: 'Over 520 Days', min: 521, max: Infinity, color: '#7f1d1d' }
];

// Global state
let STOCK_AGING_FILTERS = {
  vendor: '',
  product: '',
  search: ''
};

let STOCK_AGING_PAGINATION = {
  currentPage: 1,
  itemsPerPage: 20,
  totalItems: 0
};

// Calculate stock aging data with filters
function calculateStockAging() {
  const today = new Date();
  const agingData = {
    qtyWise: AGING_BUCKETS.map(b => ({ ...b, qty: 0, value: 0, products: [] })),
    totalQty: 0,
    totalValue: 0,
    oldStockQty: 0,
    oldStockValue: 0,
    vendors: new Set(),
    products: new Set()
  };

  if (!MASTER_DB.products || MASTER_DB.products.length === 0) {
    return agingData;
  }

  MASTER_DB.products.forEach(product => {
    if (product.vendor) agingData.vendors.add(product.vendor);
    agingData.products.add(product.title);

    if (!product.variants) return;
    
    product.variants.forEach(variant => {
      const qty = parseInt(variant.inventory_quantity || 0);
      if (qty <= 0) return;
      
      // Apply filters
      if (STOCK_AGING_FILTERS.vendor && product.vendor !== STOCK_AGING_FILTERS.vendor) return;
      if (STOCK_AGING_FILTERS.product && product.title !== STOCK_AGING_FILTERS.product) return;
      
      if (STOCK_AGING_FILTERS.search) {
        const searchLower = STOCK_AGING_FILTERS.search.toLowerCase();
        const matchTitle = product.title.toLowerCase().includes(searchLower);
        const matchSKU = (variant.sku || '').toLowerCase().includes(searchLower);
        const matchVendor = (product.vendor || '').toLowerCase().includes(searchLower);
        if (!matchTitle && !matchSKU && !matchVendor) return;
      }
      
      const price = parseFloat(variant.price || product.price || 0);
      const value = qty * price;
      
      // Get stock date from inventory items API
      let stockDate = null;
      if (variant.inventory_item_id && MASTER_DB.inventoryItems) {
        const invItem = MASTER_DB.inventoryItems.find(ii => ii.id === variant.inventory_item_id);
        if (invItem) stockDate = invItem.created_at || invItem.updated_at;
      }
      if (!stockDate) stockDate = variant.updated_at || product.updated_at || product.created_at;
      
      const daysAgo = stockDate ? Math.floor((today - new Date(stockDate)) / (1000 * 60 * 60 * 24)) : 0;
      
      const bucket = agingData.qtyWise.find(b => daysAgo >= b.min && daysAgo <= b.max);
      if (bucket) {
        bucket.qty += qty;
        bucket.value += value;
        bucket.products.push({
          title: product.title,
          variant: variant.title,
          sku: variant.sku,
          vendor: product.vendor,
          qty: qty,
          price: price,
          value: value,
          daysAgo: daysAgo,
          bucketColor: bucket.color,
          bucketLabel: bucket.label
        });
      }
      
      agingData.totalQty += qty;
      agingData.totalValue += value;
      if (daysAgo > 90) {
        agingData.oldStockQty += qty;
        agingData.oldStockValue += value;
      }
    });
  });

  return agingData;
}

// Get paginated products
function getPaginatedProducts(data) {
  const allProducts = data.qtyWise
    .flatMap(b => b.products)
    .sort((a, b) => b.daysAgo - a.daysAgo);
  
  STOCK_AGING_PAGINATION.totalItems = allProducts.length;
  
  const startIndex = (STOCK_AGING_PAGINATION.currentPage - 1) * STOCK_AGING_PAGINATION.itemsPerPage;
  const endIndex = startIndex + STOCK_AGING_PAGINATION.itemsPerPage;
  
  return allProducts.slice(startIndex, endIndex);
}

// Render pagination controls
function renderPaginationControls() {
  const totalPages = Math.ceil(STOCK_AGING_PAGINATION.totalItems / STOCK_AGING_PAGINATION.itemsPerPage);
  const currentPage = STOCK_AGING_PAGINATION.currentPage;
  
  if (totalPages <= 1) return '';
  
  let pages = [];
  
  // Previous button
  pages.push(`
    <button class="sa-page-btn ${currentPage === 1 ? 'disabled' : ''}" 
            onclick="changeStockAgingPage(${currentPage - 1})"
            ${currentPage === 1 ? 'disabled' : ''}>
      ← Prev
    </button>
  `);
  
  // Page numbers
  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
  
  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  
  if (startPage > 1) {
    pages.push(`<button class="sa-page-btn" onclick="changeStockAgingPage(1)">1</button>`);
    if (startPage > 2) pages.push(`<span class="sa-page-ellipsis">...</span>`);
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pages.push(`
      <button class="sa-page-btn ${i === currentPage ? 'active' : ''}" 
              onclick="changeStockAgingPage(${i})">
        ${i}
      </button>
    `);
  }
  
  if (endPage < totalPages) {
    if (endPage < totalPages - 1) pages.push(`<span class="sa-page-ellipsis">...</span>`);
    pages.push(`<button class="sa-page-btn" onclick="changeStockAgingPage(${totalPages})">${totalPages}</button>`);
  }
  
  // Next button
  pages.push(`
    <button class="sa-page-btn ${currentPage === totalPages ? 'disabled' : ''}" 
            onclick="changeStockAgingPage(${currentPage + 1})"
            ${currentPage === totalPages ? 'disabled' : ''}>
      Next →
    </button>
  `);
  
  return `
    <div class="sa-pagination">
      <div class="sa-pagination-info">
        Showing ${(currentPage - 1) * STOCK_AGING_PAGINATION.itemsPerPage + 1} - 
        ${Math.min(currentPage * STOCK_AGING_PAGINATION.itemsPerPage, STOCK_AGING_PAGINATION.totalItems)} 
        of ${STOCK_AGING_PAGINATION.totalItems} items
      </div>
      <div class="sa-pagination-buttons">
        ${pages.join('')}
      </div>
      <div class="sa-pagination-size">
        <label>Items per page:</label>
        <select onchange="changeStockAgingPageSize(this.value)">
          <option value="10" ${STOCK_AGING_PAGINATION.itemsPerPage === 10 ? 'selected' : ''}>10</option>
          <option value="20" ${STOCK_AGING_PAGINATION.itemsPerPage === 20 ? 'selected' : ''}>20</option>
          <option value="50" ${STOCK_AGING_PAGINATION.itemsPerPage === 50 ? 'selected' : ''}>50</option>
          <option value="100" ${STOCK_AGING_PAGINATION.itemsPerPage === 100 ? 'selected' : ''}>100</option>
        </select>
      </div>
    </div>
  `;
}

// Change page
window.changeStockAgingPage = function(page) {
  STOCK_AGING_PAGINATION.currentPage = page;
  renderStockAgingUI();
};

// Change page size
window.changeStockAgingPageSize = function(size) {
  STOCK_AGING_PAGINATION.itemsPerPage = parseInt(size);
  STOCK_AGING_PAGINATION.currentPage = 1;
  renderStockAgingUI();
};

// Render Stock Aging UI
function renderStockAgingUI() {
  const target = document.getElementById('stock-aging-target');
  if (!target) return;

  const allData = calculateStockAgingWithoutFilters();
  const vendors = Array.from(allData.vendors).sort();
  const products = Array.from(allData.products).sort();
  const data = calculateStockAging();
  
  const paginatedProducts = getPaginatedProducts(data);
  const oldStockQtyPercent = data.totalQty > 0 ? ((data.oldStockQty / data.totalQty) * 100).toFixed(0) : 0;

  target.innerHTML = `
    <div class="stock-aging-container">
      
      <!-- Header -->
      <div class="sa-header">
        <h2>📊 Stock Aging Report</h2>
        <p>As of ${new Date().toLocaleDateString('en-GB')}</p>
      </div>

      <!-- Filter Panel -->
      <div class="sa-filter-panel">
        <h3 class="sa-filter-title">🔍 Filters</h3>
        <div class="sa-filter-grid">
          
          <div class="sa-filter-group">
            <label>Search</label>
            <input type="text" class="sa-filter-input" id="sa-search" 
                   placeholder="Search SKU, Product or Vendor..." 
                   value="${STOCK_AGING_FILTERS.search}"
                   oninput="updateStockAgingFilter('search', this.value)">
          </div>
          
          <div class="sa-filter-group">
            <label>Vendor</label>
            <select class="sa-filter-select" id="sa-vendor" 
                    onchange="updateStockAgingFilter('vendor', this.value)">
              <option value="">All Vendors</option>
              ${vendors.map(v => `<option value="${v}" ${STOCK_AGING_FILTERS.vendor === v ? 'selected' : ''}>${v}</option>`).join('')}
            </select>
          </div>
          
          <div class="sa-filter-group">
            <label>Product</label>
            <select class="sa-filter-select" id="sa-product" 
                    onchange="updateStockAgingFilter('product', this.value)">
              <option value="">All Products</option>
              ${products.map(p => `<option value="${p}" ${STOCK_AGING_FILTERS.product === p ? 'selected' : ''}>${p}</option>`).join('')}
            </select>
          </div>
          
          <div class="sa-filter-reset">
            <button class="sa-btn-reset" onclick="resetStockAgingFilters()">
              🔄 Reset Filters
            </button>
          </div>
        </div>
        
        ${(STOCK_AGING_FILTERS.vendor || STOCK_AGING_FILTERS.product || STOCK_AGING_FILTERS.search) ? `
          <div class="sa-active-filters">
            <strong>Active Filters:</strong>
            ${STOCK_AGING_FILTERS.search ? `Search: "${STOCK_AGING_FILTERS.search}" | ` : ''}
            ${STOCK_AGING_FILTERS.vendor ? `Vendor: ${STOCK_AGING_FILTERS.vendor} | ` : ''}
            ${STOCK_AGING_FILTERS.product ? `Product: ${STOCK_AGING_FILTERS.product}` : ''}
            <span style="float: right;">Showing ${data.totalQty} units</span>
          </div>
        ` : ''}
      </div>

      <!-- Stats Cards -->
      <div class="sa-stats-grid">
        <div class="sa-stat-card quantity">
          <div class="sa-stat-label">Total Quantity</div>
          <div class="sa-stat-value">${formatNumber(data.totalQty)}</div>
          <div class="sa-stat-sub">${AGING_BUCKETS.length} Categories</div>
        </div>

        <div class="sa-stat-card value">
          <div class="sa-stat-label">Total Value</div>
          <div class="sa-stat-value">${formatPrice(data.totalValue)}</div>
          <div class="sa-stat-sub">Inventory Worth</div>
        </div>

        <div class="sa-stat-card ${data.oldStockQty > 0 ? 'alert pulse' : 'success'}">
          <div class="sa-stat-label">
            ${data.oldStockQty > 0 ? '⚠️ Old Stock (>90 Days)' : '✅ Stock Healthy'}
          </div>
          <div class="sa-stat-value">${oldStockQtyPercent}%</div>
          <div class="sa-stat-sub">
            ${formatNumber(data.oldStockQty)} units | ${formatPrice(data.oldStockValue)}
          </div>
        </div>
      </div>

      <!-- Tables Section -->
      <div class="sa-tables-grid">
        
        <!-- Quantity Table -->
        <div class="sa-table-card">
          <h3 class="sa-table-title">📦 Quantity Wise Aging</h3>
          <table class="sa-table">
            <thead>
              <tr>
                <th>Age Bucket</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">%</th>
              </tr>
            </thead>
            <tbody>
              ${data.qtyWise.map((bucket, index) => {
                const percent = data.totalQty > 0 ? ((bucket.qty / data.totalQty) * 100).toFixed(1) : 0;
                const isOld = bucket.min > 90;
                return `
                  <tr class="${isOld ? 'old-stock' : ''} ${bucket.qty === 0 ? 'empty-row' : ''}">
                    <td>
                      <span class="sa-color-box" style="background: ${bucket.color}"></span>
                      <span class="${isOld ? 'sa-text-old' : 'sa-text-normal'}">${bucket.label}</span>
                      ${isOld ? ' 🔴' : ''}
                    </td>
                    <td class="text-right ${isOld ? 'sa-text-old' : ''}" style="font-weight: 600;">
                      ${formatNumber(bucket.qty)}
                    </td>
                    <td class="text-right sa-text-muted">${percent}%</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td class="text-right">${formatNumber(data.totalQty)}</td>
                <td class="text-right">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Value Table -->
        <div class="sa-table-card">
          <h3 class="sa-table-title">💰 Value Wise Aging</h3>
          <table class="sa-table">
            <thead>
              <tr>
                <th>Age Bucket</th>
                <th class="text-right">Value</th>
                <th class="text-right">%</th>
              </tr>
            </thead>
            <tbody>
              ${data.qtyWise.map((bucket, index) => {
                const percent = data.totalValue > 0 ? ((bucket.value / data.totalValue) * 100).toFixed(1) : 0;
                const isOld = bucket.min > 90;
                return `
                  <tr class="${isOld ? 'old-stock' : ''} ${bucket.value === 0 ? 'empty-row' : ''}">
                    <td>
                      <span class="sa-color-box" style="background: ${bucket.color}"></span>
                      <span class="${isOld ? 'sa-text-old' : 'sa-text-normal'}">${bucket.label}</span>
                      ${isOld ? ' 🔴' : ''}
                    </td>
                    <td class="text-right ${isOld ? 'sa-text-old' : ''}" style="font-weight: 600;">
                      ${formatPrice(bucket.value)}
                    </td>
                    <td class="text-right sa-text-muted">${percent}%</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td class="text-right">${formatPrice(data.totalValue)}</td>
                <td class="text-right">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Charts -->
      <div class="sa-charts-grid">
        <div class="sa-chart-card">
          <h3 class="sa-chart-title">Quantity Distribution</h3>
          <canvas id="qtyChart" class="sa-chart-canvas" width="400" height="300"></canvas>
        </div>
        <div class="sa-chart-card">
          <h3 class="sa-chart-title">Value Distribution</h3>
          <canvas id="valueChart" class="sa-chart-canvas" width="400" height="300"></canvas>
        </div>
      </div>

      <!-- Product Details with Pagination -->
      ${data.totalQty > 0 ? `
      <div class="sa-details-card">
        <h3 class="sa-details-title">
          📋 Product Details 
          <span class="sa-details-count">(${STOCK_AGING_PAGINATION.totalItems} items)</span>
        </h3>
        
        ${renderPaginationControls()}
        
        <div class="sa-details-table-wrapper">
          <table class="sa-details-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Variant</th>
                <th>Vendor</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Price</th>
                <th class="text-right">Value</th>
                <th class="text-right">Age</th>
                <th style="text-align: center;">Bucket</th>
              </tr>
            </thead>
            <tbody>
              ${paginatedProducts.map(p => `
                <tr>
                  <td class="sa-product-name">${p.title}</td>
                  <td class="sa-variant-name">${p.variant}</td>
                  <td class="sa-vendor-name">${p.vendor || 'N/A'}</td>
                  <td class="sa-qty-cell">${p.qty}</td>
                  <td class="sa-price-cell">${formatPrice(p.price)}</td>
                  <td class="sa-value-cell ${p.daysAgo > 90 ? 'old' : ''}">${formatPrice(p.value)}</td>
                  <td class="text-right">
                    <span class="sa-age-badge ${p.daysAgo > 365 ? 'old' : p.daysAgo > 90 ? 'medium' : 'new'}">
                      ${p.daysAgo}d
                    </span>
                  </td>
                  <td style="text-align: center;">
                    <span class="sa-bucket-dot" style="background: ${p.bucketColor}" title="${p.bucketLabel}"></span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        ${renderPaginationControls()}
      </div>
      ` : '<div class="sa-no-data">No products match the selected filters.</div>'}

      <!-- Export Buttons -->
      <div class="sa-export-section">
        <button class="sa-btn-export pdf" onclick="exportStockAging('pdf')">📄 Export PDF</button>
        <button class="sa-btn-export excel" onclick="exportStockAging('excel')">📈 Export Excel</button>
      </div>
    </div>
  `;

  setTimeout(() => {
    renderPieChart('qtyChart', data.qtyWise, 'qty');
    renderPieChart('valueChart', data.qtyWise, 'value');
  }, 100);
}

// Helper functions (same as before)
function calculateStockAgingWithoutFilters() {
  const temp = { ...STOCK_AGING_FILTERS };
  STOCK_AGING_FILTERS = { vendor: '', product: '', search: '' };
  const data = calculateStockAging();
  STOCK_AGING_FILTERS = temp;
  return data;
}

window.updateStockAgingFilter = function(type, value) {
  STOCK_AGING_FILTERS[type] = value;
  STOCK_AGING_PAGINATION.currentPage = 1; // Reset to first page on filter change
  renderStockAgingUI();
};

window.resetStockAgingFilters = function() {
  STOCK_AGING_FILTERS = { vendor: '', product: '', search: '' };
  STOCK_AGING_PAGINATION.currentPage = 1;
  renderStockAgingUI();
};

function renderPieChart(canvasId, data, type) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const radius = Math.min(centerX, centerY) - 50;
  const total = data.reduce((sum, b) => sum + b[type], 0);
  
  if (total === 0) {
    ctx.fillStyle = '#9ca3af';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('No Data', centerX, centerY);
    return;
  }
  
  let currentAngle = -Math.PI / 2;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  data.forEach(bucket => {
    const value = bucket[type];
    if (value <= 0) return;
    const sliceAngle = (value / total) * 2 * Math.PI;
    
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
    ctx.closePath();
    ctx.fillStyle = bucket.color;
    ctx.fill();
    
    if (sliceAngle > 0.3) {
      const labelAngle = currentAngle + sliceAngle / 2;
      const labelX = centerX + Math.cos(labelAngle) * (radius * 0.7);
      const labelY = centerY + Math.sin(labelAngle) * (radius * 0.7);
      ctx.fillStyle = 'white';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${((value/total)*100).toFixed(0)}%`, labelX, labelY);
    }
    currentAngle += sliceAngle;
  });
  
  let legendY = 30;
  const legendX = canvas.width - 140;
  data.forEach(bucket => {
    if (bucket[type] <= 0) return;
    ctx.fillStyle = bucket.color;
    ctx.fillRect(legendX, legendY, 12, 12);
    ctx.fillStyle = '#374151';
    ctx.font = '11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`${bucket.label}: ${((bucket[type]/total)*100).toFixed(0)}%`, legendX + 18, legendY + 9);
    legendY += 20;
  });
}

function formatNumber(num) {
  return parseFloat(num || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

window.exportStockAging = function(type) {
  const data = calculateStockAging();
  type === 'pdf' ? generateStockAgingPDF(data) : generateStockAgingExcel(data);
};

function generateStockAgingPDF(data) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF('p', 'mm', 'a4');
  
  doc.setFontSize(20);
  doc.text('Stock Aging Report', 105, 20, { align: 'center' });
  
  let filterText = 'All Products';
  if (STOCK_AGING_FILTERS.vendor) filterText = `Vendor: ${STOCK_AGING_FILTERS.vendor}`;
  if (STOCK_AGING_FILTERS.product) filterText += ` | Product: ${STOCK_AGING_FILTERS.product}`;
  
  doc.setFontSize(10);
  doc.text(`Filters: ${filterText}`, 105, 28, { align: 'center' });
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 33, { align: 'center' });
  
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(14, 40, 182, 25, 5, 5, 'F');
  doc.setFontSize(11);
  doc.text(`Total Qty: ${formatNumber(data.totalQty)} | Total Value: ${formatPrice(data.totalValue)} | Old Stock: ${formatNumber(data.oldStockQty)}`, 20, 55);
  
  const head = [['Age Bucket', 'Quantity', 'Qty %', 'Value', 'Value %']];
  const body = data.qtyWise.map(b => [
    b.label,
    formatNumber(b.qty),
    data.totalQty > 0 ? ((b.qty/data.totalQty)*100).toFixed(1) + '%' : '0%',
    formatPrice(b.value),
    data.totalValue > 0 ? ((b.value/data.totalValue)*100).toFixed(1) + '%' : '0%'
  ]);
  
  doc.autoTable({ startY: 75, head, body, theme: 'striped', headStyles: { fillColor: [102, 126, 234], textColor: 255 } });
  doc.save(`Stock_Aging_${Date.now()}.pdf`);
}

function generateStockAgingExcel(data) {
  let csv = "data:text/csv;charset=utf-8,Stock Aging Report\n";
  csv += `Generated,${new Date().toLocaleDateString()}\n`;
  csv += `Filters,${STOCK_AGING_FILTERS.vendor || 'All'},${STOCK_AGING_FILTERS.product || 'All'}\n\n`;
  csv += "Age Bucket,Quantity,Qty %,Value,Value %\n";
  data.qtyWise.forEach(b => {
    csv += `${b.label},${b.qty},${data.totalQty > 0 ? ((b.qty/data.totalQty)*100).toFixed(1) : 0}%,${b.value},${data.totalValue > 0 ? ((b.value/data.totalValue)*100).toFixed(1) : 0}%\n`;
  });
  
  // Add all products (not paginated for export)
  const allProducts = data.qtyWise.flatMap(b => b.products).sort((a, b) => b.daysAgo - a.daysAgo);
  if (allProducts.length > 0) {
    csv += `\n\nProduct Details\n`;
    csv += "Product,Variant,Vendor,SKU,Quantity,Price,Value,Age Days,Age Bucket\n";
    allProducts.forEach(p => {
      csv += `"${p.title}","${p.variant}","${p.vendor || ''}","${p.sku || ''}",${p.qty},${p.price},${p.value},${p.daysAgo},${p.bucketLabel}\n`;
    });
  }
  
  const link = document.createElement("a");
  link.setAttribute("href", encodeURI(csv));
  link.setAttribute("download", `Stock_Aging_${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

window.initStockAgingModule = function() {
  STOCK_AGING_FILTERS = { vendor: '', product: '', search: '' };
  STOCK_AGING_PAGINATION = { currentPage: 1, itemsPerPage: 20, totalItems: 0 };
  renderStockAgingUI();
};